import Phaser from "phaser";
import { GAME } from "@/config";
import { PALETTE, PALETTE_HEX } from "@/palette";
import { buildHero, fitFacilityName, HERO_THEME } from "./hero";
import { brokerLead, recruit, recruiterUpgradeCost, upgradeRecruiter } from "@/systems/actions";
import type { TickEvent } from "@/systems/engine";
import { runtime } from "@/systems/runtime";
import { bakeAllCharacterTextures, Character } from "@/sprites/Character";
import { artTextureKey, isArtLoaded, resolvePatientTexture } from "@/sprites/artManifest";
import { bakeJuiceTextures, cashRain, debris, flash, shake } from "@/sprites/juice";
import { labels, showGagModal, type ActionSpec, type OverlayHandles } from "@/ui/dom";
import { OWNER_QUIPS, TAP_GAGS } from "@/data/tapGags";
import { METERS, OFFICE } from "@/data/tunables";
import {
  DIRT_H,
  FLOOR_INSET,
  GROUND_BOTTOM,
  OFFICE_VISUALS,
  OFFICE_WORLD_H,
  PANEL_HS,
  PANEL_TOP,
  PANEL_W,
  ROOF_H,
  ROOF_TOP,
} from "@/data/visuals";
import { StageLayer } from "./stage";
import { wirePan } from "./pan";

/**
 * THE OFFICE (RFC §7–8): recruit leads at the door; they WALK to the intake
 * line (never slot cards — exclusions ledger). Renders the office end of the
 * shared queue; Detox admits from the same structure. Scene sleeps/wakes via
 * the tab bar and reconciles from GameState on wake (sleeping scenes still
 * receive events, so handlers gate on isActive — Phaser scenes skill #8).
 */

const DOOR_X = 150;
const QUEUE_FRONT_X = 900;
const QUEUE_SPACING = 128;
const EXIT_RIGHT = GAME.width + 140;
// Office functional floor — the GROUND panel's standable baseline in the
// pannable world (v0.9): bottom of the stacked panels minus the floor inset.
const FLOOR = GROUND_BOTTOM - FLOOR_INSET;
/** Art-absent fallback: with no panels there is no pan — the queue must
 * live inside the fixed viewport (the old pre-dollhouse floor line). */
const FLOOR_FALLBACK = 2410;

export class OfficeScene extends Phaser.Scene {
  /** Lead-queue floor line: the dollhouse ground floor, or the on-screen
   * fallback when panel art is absent (no pan = no off-screen floor). */
  private floorY = FLOOR;
  private overlay!: OverlayHandles;
  private leadSprites = new Map<number, Character>();
  private facilityBar: Phaser.GameObjects.Text | null = null;
  private unsubscribe: (() => void) | undefined;
  private stage: StageLayer | null = null;
  private ownerSprite: Phaser.GameObjects.Sprite | null = null;

  constructor() {
    super("office");
  }

  init(data: { overlay: OverlayHandles }): void {
    this.overlay = data.overlay;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE_HEX.mustard); // warm walls (hero-banner canon)
    bakeAllCharacterTextures(this);
    bakeJuiceTextures(this);
    this.drawStage();
    this.reconcile();

    this.unsubscribe = runtime.subscribe((events) => {
      if (!this.scene.isActive()) return; // reconcile() on wake covers it
      this.onEvents(events);
      this.stage?.sync(runtime.state); // progression props appear/vanish live
    });
    const onWake = () => {
      this.reconcile();
      this.installActions();
    };
    this.events.on(Phaser.Scenes.Events.WAKE, onWake);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
      this.events.off(Phaser.Scenes.Events.WAKE, onWake);
    });
    this.installActions();
  }

  private drawStage(): void {
    // Shared hero banner (pinned chrome) — built unconditionally: it must
    // survive absent dollhouse art (the graceful-fallback contract).
    this.facilityBar = buildHero(this, {
      titleKey: "hero-office",
      titleText: "OFFICE",
      ownerKey: "hero-owner",
      motto: "WELLNESS, PROFITABLY",
      theme: HERO_THEME.office,
    });
    // v0.9 dollhouse: three room-scale floor PANELS stacked into a pannable
    // world + the visuals slot table — every furnishing is a layered prop
    // keyed to state. Board A stays the design reference.
    const panelsLoaded = [0, 1, 2].every((i) => isArtLoaded(`doll-floor-${i}`));
    this.floorY = panelsLoaded ? FLOOR : FLOOR_FALLBACK;
    if (panelsLoaded) {
      // Stack the board-derived panels top-down (manifest index 0 = ground);
      // each keeps its own aspect-true display height.
      let bottom = PANEL_TOP;
      for (const i of [2, 1, 0]) {
        bottom += PANEL_HS[i]!;
        this.add
          .image(GAME.width / 2, bottom, artTextureKey(`doll-floor-${i}`))
          .setOrigin(0.5, 1)
          .setDisplaySize(PANEL_W, PANEL_HS[i]!)
          .setDepth(-100);
      }
      // Roofline band above (the billboard gag slot — art lands later) and
      // the dirt easter-egg layer below; both procedural placeholders.
      this.add
        .rectangle(GAME.width / 2, ROOF_TOP + ROOF_H, GAME.width, 26, PALETTE.charcoal)
        .setOrigin(0.5, 1)
        .setDepth(-90);
      this.add
        .rectangle(GAME.width / 2, OFFICE_WORLD_H, GAME.width, DIRT_H, 0x3a2e22)
        .setOrigin(0.5, 1)
        .setDepth(-95);
      this.stage = new StageLayer(this, OFFICE_VISUALS);
      this.stage.sync(runtime.state);
      this.spawnOwner();
      // Drag to pan the building; park with the GROUND floor at the screen
      // bottom (active gameplay in view; dirt discovered by panning down).
      wirePan(this, OFFICE_WORLD_H);
      this.cameras.main.scrollY = GROUND_BOTTOM - GAME.height;
      // The big reception sign — PROCEDURAL (two Gemini extractions both
      // hallucinated a headset-man poster; a blank board is two rects).
      const groundBase = OFFICE_VISUALS.floorBaselines[0]! * OFFICE_VISUALS.worldHeight;
      const signX = 0.40 * GAME.width;
      const signCY = groundBase - 460;
      const signW = 420;
      const signH = 230;
      this.add
        .rectangle(signX, signCY, signW, signH, PALETTE.paper)
        .setStrokeStyle(10, PALETTE.charcoal)
        .setDepth(-40);
      this.add
        .text(signX, signCY - 38, "INTAKE", {
          fontFamily: "Georgia, serif", fontSize: "56px", fontStyle: "bold", color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-39);
      this.add
        .text(signX, signCY + 48, "now serving: everyone*", {
          fontFamily: "Georgia, serif", fontSize: "26px", color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-39);
      // Curated tap-a-prop (ruling 8): stagetap → topmost slot → gag modal.
      // The owner gets his quip ladder instead (escalates with the moral arc).
      this.events.on("stagetap", this.onStageTap, this);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.events.off("stagetap", this.onStageTap, this);
      });
    }
  }

  private onStageTap(wx: number, wy: number): void {
    if (this.ownerSprite?.active && this.ownerSprite.getBounds().contains(wx, wy)) {
      const st = runtime.state;
      const pool = st.run.criminal.unlocked
        ? OWNER_QUIPS.criminal
        : st.account.reputationCapital > 0
          ? OWNER_QUIPS.prestige
          : OWNER_QUIPS.early;
      const quip = pool[Phaser.Math.Between(0, pool.length - 1)]!; // visual-only RNG
      this.floatText(this.ownerSprite.x, this.ownerSprite.y - 380, quip, PALETTE_HEX.charcoal, 2600);
      return;
    }
    const key = this.stage?.hitTest(wx, wy);
    const gag = key ? TAP_GAGS[key] : undefined;
    if (gag) showGagModal(gag.title, gag.lines);
  }

  /**
   * The OWNER as a little actor (v0.9): 4-frame skeleton-leg walk sheet.
   * Paces his top-floor office on an ambient timer (visual randomness only —
   * never the sim RNG) and always renders BEHIND the desk (walk-behind
   * staging). Absent sheet → the static doll-owner slot covers it.
   */
  private spawnOwner(): void {
    if (!isArtLoaded("doll-owner-walk")) return;
    const v = OFFICE_VISUALS;
    const baseY = v.floorBaselines[2]! * v.worldHeight;
    const key = artTextureKey("doll-owner-walk");
    if (!this.anims.exists("owner-walk")) {
      // Russell's cycle (2026-07-17): last-4 stride frames, ping-pong
      // 1,2,3,4,3,2 — no wrap snap.
      this.anims.create({
        key: "owner-walk",
        frames: this.anims.generateFrameNumbers(key, { frames: [0, 1, 2, 3, 2, 1] }),
        frameRate: 9,
        repeat: -1,
      });
    }
    const s = this.add.sprite(GAME.width * 0.3, baseY, key, 0).setOrigin(0.5, 1);
    this.ownerSprite = s;
    // Idle = the approved standing frame (the static doll-owner texture).
    const idle = (): void => {
      s.stop();
      if (isArtLoaded("doll-owner")) s.setTexture(artTextureKey("doll-owner"));
    };
    idle();
    s.setDepth(baseY + 20); // z2: behind the desk's z3 (baseY + 30)
    const schedule = (): void => {
      this.time.delayedCall(Phaser.Math.Between(3500, 9000), pace);
    };
    const pace = (): void => {
      if (!s.active) return;
      const targetX = Phaser.Math.Between(Math.round(GAME.width * 0.1), Math.round(GAME.width * 0.55));
      if (Math.abs(targetX - s.x) < 60) {
        schedule();
        return;
      }
      s.setFlipX(targetX < s.x); // the sheet walks rightward natively
      s.setTexture(key, 0);
      s.play("owner-walk");
      this.tweens.add({
        targets: s,
        x: targetX,
        duration: Math.abs(targetX - s.x) * 6,
        onComplete: () => {
          if (!s.active) return; // scene may have shut down mid-pace
          idle();
          schedule();
        },
      });
    };
    schedule();
  }

  /** Rebuild lead sprites to match the queue exactly (wake/create path). */
  private reconcile(): void {
    const queue = runtime.state.run.queue;
    const live = new Set(queue.map((l) => l.id));
    for (const [id, sprite] of this.leadSprites) {
      if (!live.has(id)) {
        sprite.destroy();
        this.leadSprites.delete(id);
      }
    }
    queue.forEach((lead, i) => {
      const x = QUEUE_FRONT_X - i * QUEUE_SPACING;
      const existing = this.leadSprites.get(lead.id);
      if (existing) {
        existing.walkTo(x, this.floorY);
      } else {
        const c = new Character(this, x, this.floorY, resolvePatientTexture(lead.id));
        this.leadSprites.set(lead.id, c);
      }
    });
  }

  /** Keep the facility-name bar current (cheap; only refits when the name changes). */
  private syncFacility(): void {
    if (this.facilityBar !== null) fitFacilityName(this.facilityBar);
  }

  private onEvents(events: TickEvent[]): void {
    this.syncFacility(); // pick up a rename made while on the office scene
    for (const ev of events) {
      switch (ev.type) {
        case "leadJoined": {
          const i = runtime.state.run.queue.findIndex((l) => l.id === ev.leadId);
          const x = QUEUE_FRONT_X - Math.max(0, i) * QUEUE_SPACING;
          const c = new Character(this, DOOR_X, this.floorY, resolvePatientTexture(ev.leadId));
          this.leadSprites.set(ev.leadId, c);
          c.walkTo(x, this.floorY);
          break;
        }
        case "leadBalked": {
          // transient fleeing sprite — no persisted id; vary by name so the
          // face isn't always the same walk-away guy.
          const variant = [...ev.name].reduce((a, ch) => a + ch.charCodeAt(0), 0);
          const c = new Character(this, DOOR_X, this.floorY, resolvePatientTexture(variant));
          this.floatText(DOOR_X + 170, this.floorY - 420, "NO VACANCY*", PALETTE_HEX.coralRed);
          c.walkTo(DOOR_X + 180, this.floorY, () => c.balkAndLeave(-160));
          break;
        }
        case "admitted": {
          const sprite = this.leadSprites.get(ev.patientId);
          if (sprite) {
            this.leadSprites.delete(ev.patientId);
            sprite.walkTo(EXIT_RIGHT, this.floorY, () => sprite.destroy());
          }
          // remaining line steps forward
          this.reconcileLinePositions();
          break;
        }
        case "upgraded":
          break;
        case "brokerUnlocked":
          // the invitation may be read WHILE on the Office (autoTravel is a
          // no-op then) — rebuild the action bar so BROKER appears immediately.
          this.installActions();
          break;
        case "brokered": {
          const sprite = this.leadSprites.get(ev.leadId);
          if (sprite) {
            this.leadSprites.delete(ev.leadId);
            sprite.walkTo(EXIT_RIGHT, this.floorY, () => sprite.destroy());
          }
          this.reconcileLinePositions();
          cashRain(this, 660, this.cameras.main.scrollY + 1420); // v0.7 juice — the broker's cash (camera-relative in the pannable world)
          break;
        }
        case "gameOver": {
          flash(this); // v0.7 juice — the game-over alarm
          shake(this);
          if (ev.reason === "raid") debris(this, GAME.width / 2, this.cameras.main.scrollY + 1000);
          break;
        }
        default:
          break;
      }
    }
  }

  private reconcileLinePositions(): void {
    runtime.state.run.queue.forEach((lead, i) => {
      const sprite = this.leadSprites.get(lead.id);
      sprite?.walkTo(QUEUE_FRONT_X - i * QUEUE_SPACING, this.floorY);
    });
  }

  private installActions(): void {
    this.overlay.setActiveTab("office");
    const specs: ActionSpec[] = [
      {
        label: labels.recruit,
        icon: "assets/tiles/recruit.webp",
        onTap: () => runtime.act([recruit(runtime.state)]),
      },
      {
        label: labels.upgradeRecruiter,
        icon: "assets/tiles/hire.webp",
        secondary: true,
        disabled: () => {
          const max = OFFICE.recruiterIntervalByLevel.length - 1;
          return (
            runtime.state.run.office.recruiterLevel >= max ||
            runtime.state.run.funding < recruiterUpgradeCost(runtime.state)
          );
        },
        onTap: () => runtime.act([upgradeRecruiter(runtime.state)]),
      },
    ];
    // Body broker (v0.5): appears once unlocked; usable ONLY at a full line.
    if (runtime.state.run.brokerUnlocked) {
      specs.push({
        label: () =>
          runtime.state.run.queue.length >= OFFICE.queueCap
            ? `Broker +$${METERS.brokerPayout}`
            : "Broker",
        icon: "assets/tiles/broker.webp",
        secondary: true,
        disabled: () =>
          runtime.state.run.queue.length < OFFICE.queueCap ||
          runtime.state.run.tick < runtime.state.run.brokerLockoutUntil,
        // Radial veil mirrors the machine's lockout (v0.10 M3) — same
        // pattern as the alumni Contact tile.
        cooldown: () => {
          const remaining = runtime.state.run.brokerLockoutUntil - runtime.state.run.tick;
          return Math.max(0, Math.min(1, remaining / METERS.brokerCooldownTicks));
        },
        onTap: () => {
          try {
            const ev = brokerLead(runtime.state);
            runtime.act([ev]);
            this.floatText(660, this.cameras.main.scrollY + 1500, "🚐  …gone", "#C0392B");
          } catch (err) {
            console.error("halfway: broker failed", err);
          }
        },
      });
    }
    this.overlay.setActions(specs);
  }

  /** Rising fade-out text. `holdMs` keeps it readable in place first (quips). */
  private floatText(x: number, y: number, msg: string, color: string, holdMs = 0): void {
    const t = this.add
      .text(x, y, msg, {
        fontFamily: "Georgia, serif",
        fontSize: "44px",
        fontStyle: "bold",
        color,
        // Held text is SPEECH — give it a paper bubble so it reads over art.
        ...(holdMs > 0 ? { backgroundColor: PALETTE_HEX.paper, padding: { x: 16, y: 10 } } : {}),
      })
      .setOrigin(0.5, 1)
      .setDepth(3000);
    // keep speech bubbles on-canvas (edge actors would clip otherwise)
    t.x = Math.max(t.displayWidth / 2 + 16, Math.min(GAME.width - t.displayWidth / 2 - 16, t.x));
    this.tweens.add({
      targets: t,
      y: y - 90,
      alpha: 0,
      delay: holdMs,
      duration: 950,
      ease: "Cubic.easeOut",
      onComplete: () => t.destroy(),
    });
  }
}
